import {
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { useFormikContext } from "formik";
import React, { Fragment, useEffect, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useMeasure } from "react-use";
import styled from "styled-components";
import { TGetContactsResponse } from "../../../../../../../../api/contact/apiGetContacts";
import {
  FormikSelect,
  FormikTextField,
} from "../../../../../../../../components/FormikFields";
import cuideoTheme from "../../../../../../../../containers/themes/defaultTheme";
import useContactsFindQuery from "../../../../../../../../queries/contact/useContactsFindQuery";
import useContactUniqueEmailQuery from "../../../../../../../../queries/contact/useContactUniqueEmailQuery";
import useContactUniquePhoneQuery from "../../../../../../../../queries/contact/useContactUniquePhoneQuery";
import useDebounce from "../../../../../../../../util/useDebounce";
import DebouncedSearchField from "../../../../../../components/DebouncedSearchField";
import ViewInZohoButton from "../../../../../../components/ViewInZohoButton";
import { TContactRelationshipOp, TSelectOption } from "../../types";

const StyledRadioGroup = styled(RadioGroup)`
  flex-direction: row;
  flex-wrap: nowrap;

  .MuiSvgIcon-root {
    width: 1.25rem;
    height: 1.25rem;
  }

  .MuiFormControlLabel-root {
    width: 12rem;
  }

  .MuiFormControlLabel-label {
    font-size: 0.875rem;
    color: ${cuideoTheme.palette.primary.light};
  }
`;

const StyledWrapper = styled(Box)`
  transition: min-height 0.25s ease-in-out, height 0.25s ease-in-out;
  overflow: hidden;
  padding: 0.25rem 0;
`;

const StyledInputWrapper = styled.div`
  margin-bottom: 1rem;
`;

const StyledTableWrapper = styled(Box)`
  border-top: 2px solid rgba(0, 72, 120, 0.25);
  border-bottom: 2px solid rgba(0, 72, 120, 0.25);
  min-height: 12.5rem;
`;

const StyledTable = styled(Table)`
  table-layout: fixed;

  .MuiTableCell-root {
    border-bottom: none;
  }

  .MuiTableCell-body {
    user-select: none;
  }

  .MuiTableCell-body:nth-child(1) {
    width: 14rem;
  }
  .MuiTableCell-body:nth-child(2) {
    width: 5rem;
  }
  .MuiTableCell-body:nth-child(3) {
    width: 5rem;
  }
  .MuiTableCell-body:nth-child(4) {
    width: 6rem;
    text-align: right;
  }
`;

const StyledBodyRow = styled(TableRow)`
  background-color: #fff;
  transition: background-color 0.125s ease-in-out;
  .MuiButton-root {
    transition: opacity 0.125s ease-in-out;
    opacity: 0;
    pointer-events: none;

    &:hover,
    &:focus {
      opacity: 1;
      pointer-events: unset;
    }
  }

  &:hover,
  &:focus {
    .MuiButton-root {
      opacity: 1;
      pointer-events: unset;
    }
    background-color: rgba(235, 194, 0, 0.1);
  }
`;

const StyledOneRow = styled(TableRow)`
  height: 12.5rem;
`;

const StyledCircularProgressWrapper = styled(Box)`
  margin: 0.25rem auto;
  text-align: center;
`;

const StyledCircularProgress = styled(CircularProgress)``;

const StyledOneCell = styled(TableCell)``;

const StyledOneCellMessage = styled(Typography)`
  font-size: 0.875rem;
  color: #777;
  text-align: center;
  margin: 1rem 2rem;
`;

const StyledTableBodyCell = styled(TableCell)`
  font-size: 0.875rem;
  color: ${cuideoTheme.palette.primary.main};
  line-height: 1.25;
  padding: 0.25rem 0.5rem;
`;

const StyledInTableButton = styled(Button)`
  text-transform: none;
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
`;

const StyledAlert = styled(Alert)`
  font-size: 0.875rem;
  line-height: 1.35;
  margin: 0.25rem 0;
`;

type TContact = {
  id: string;
  First_Name?: string;
  Last_Name?: string;
  Phone?: string;
  Email?: string;
};

export type TContactRelationModalForm = {
  apiOp: TContactRelationshipOp | null;
  op: "new" | "existing" | "";
  id?: string;
  contact_id?: string;
  First_Name: string;
  Last_Name: string;
  Phone: string;
  Email: string;
  email_used_by: {
    id: string;
    crm_id?: string;
    First_Name?: string;
    Last_Name?: string;
  } | null;
  phone_used_by: {
    id: string;
    crm_id?: string;
    First_Name?: string;
    Last_Name?: string;
  } | null;
  Parentesco: TSelectOption;
};

interface IContactRelationModalFormProps {
  onCancel: () => void;
  kinshipOptions: { value: string; label: string }[];
}

const ContactRelationModalForm = (props: IContactRelationModalFormProps) => {
  const { onCancel, kinshipOptions } = props;
  const formikContext = useFormikContext<TContactRelationModalForm>();
  const { formatMessage } = useIntl();
  const ref = useRef<HTMLInputElement>(null);
  const [innerRef, { height }] = useMeasure();
  const [state, setState] = useState({
    searchText: "",
  });
  const contactQuery = useContactsFindQuery(state.searchText);
  const debouncedEmail = useDebounce(formikContext.values.Email, 500);
  const contactUniqueEmailQuery = useContactUniqueEmailQuery(debouncedEmail);
  const debouncedPhone = useDebounce(formikContext.values.Phone, 500);
  const contactUniquePhoneQuery = useContactUniquePhoneQuery(debouncedPhone);

  useEffect(() => {
    if (contactUniqueEmailQuery.data && contactUniqueEmailQuery.data[0]) {
      const contact = contactUniqueEmailQuery.data[0];
      if (
        !formikContext.values.email_used_by ||
        formikContext.values.email_used_by.id !== contact.id
      ) {
        formikContext.setFieldValue("email_used_by", {
          id: contact.id,
          crm_id: contact.zohoId,
          First_Name: contact.First_Name,
          Last_Name: contact.Last_Name,
        });
      }
    } else {
      if (formikContext.values.email_used_by) {
        formikContext.setFieldValue("email_used_by", null);
      }
    }
  }, [contactUniqueEmailQuery, formikContext]);

  useEffect(() => {
    if (contactUniquePhoneQuery.data && contactUniquePhoneQuery.data[0]) {
      const contact = contactUniquePhoneQuery.data[0];
      if (
        !formikContext.values.phone_used_by ||
        formikContext.values.phone_used_by.id !== contact.id
      ) {
        formikContext.setFieldValue("phone_used_by", {
          id: contact.id,
          crm_id: contact.zohoId,
          First_Name: contact.First_Name,
          Last_Name: contact.Last_Name,
        });
      }
    } else {
      if (formikContext.values.phone_used_by) {
        formikContext.setFieldValue("phone_used_by", null);
      }
    }
  }, [contactUniquePhoneQuery, formikContext]);

  const handleSourceChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    value: string
  ) => {
    formikContext.setFieldValue("op", value);
    formikContext.setValues({
      op: value === "existing" ? "existing" : "new",
      contact_id: "",
      apiOp: null,
      First_Name: "",
      Last_Name: "",
      Phone: "",
      phone_used_by: null,
      Email: "",
      email_used_by: null,
      Parentesco: null,
    });
  };

  const handleSearchChange = (newSearchTerm: string) => {
    if (newSearchTerm !== state.searchText) {
      setState({
        ...state,
        searchText: newSearchTerm,
      });
    }
  };

  const handleSelectRow = (contact: TGetContactsResponse) => {
    formikContext.setValues({
      ...formikContext.values,
      Email: contact.Email ?? "",
      First_Name: contact?.First_Name ?? "",
      Last_Name: contact?.Last_Name ?? "",
      Phone: contact?.Phone ?? "",
      contact_id: contact.id,
    });
  };

  return (
    <Fragment>
      <DialogContent>
        {!formikContext.values.apiOp && (
          <FormControl component="fieldset">
            <StyledRadioGroup
              name="op"
              value={formikContext.values.op}
              onChange={handleSourceChange}
            >
              <FormControlLabel
                value="new"
                control={<Radio color="primary" />}
                label={formatMessage({
                  id: "ContactRelationModalForm.New",
                  defaultMessage: "Nuevo contacto",
                })}
              />
              <FormControlLabel
                value="existing"
                control={<Radio color="primary" />}
                label={formatMessage({
                  id: "ContactRelationModalForm.Existing",
                  defaultMessage: "Contacto existente",
                })}
              />
            </StyledRadioGroup>
          </FormControl>
        )}
        <StyledWrapper
          style={
            formikContext.values.op === "existing"
              ? {
                  minHeight: `${height}px`,
                  height: `${height}px`,
                }
              : {
                  minHeight: `${height}px`,
                  height: `${height}px`,
                }
          }
        >
          <div ref={innerRef as any}>
            {formikContext.values.op === "existing" &&
              !formikContext.values.contact_id && (
                <Fragment>
                  <StyledInputWrapper>
                    <DebouncedSearchField
                      ref={ref}
                      initialValue={state.searchText}
                      onDebouncedChange={handleSearchChange}
                      placeholder={formatMessage({
                        id: "SearchField.Placeholder",
                        defaultMessage: "Buscar",
                      })}
                    />
                  </StyledInputWrapper>
                  <StyledTableWrapper>
                    <StyledTable>
                      <TableBody>
                        {state.searchText === "" ? (
                          <StyledOneRow>
                            <StyledOneCell colSpan={4}>
                              <StyledOneCellMessage>
                                <FormattedMessage
                                  id="ContactRelationModalForm.StartTyping"
                                  defaultMessage="Empieza a escribir un nombre, apellido, Email o teléfono"
                                />
                              </StyledOneCellMessage>
                            </StyledOneCell>
                          </StyledOneRow>
                        ) : contactQuery.isLoading ? (
                          <StyledOneRow>
                            <StyledOneCell colSpan={4}>
                              <StyledCircularProgressWrapper>
                                <StyledCircularProgress size={22} />
                              </StyledCircularProgressWrapper>
                              <StyledOneCellMessage>
                                <FormattedMessage
                                  id="ContactRelationModalForm.Loading"
                                  defaultMessage="Buscando Contactos..."
                                />
                              </StyledOneCellMessage>
                            </StyledOneCell>
                          </StyledOneRow>
                        ) : contactQuery.data?.totalItems === 0 ? (
                          <StyledOneRow>
                            <StyledOneCell colSpan={4}>
                              <StyledOneCellMessage>
                                <FormattedMessage
                                  id="ContactRelationModalForm.NoResults"
                                  defaultMessage="No se han encontrado resultados"
                                />
                              </StyledOneCellMessage>
                            </StyledOneCell>
                          </StyledOneRow>
                        ) : (
                          <Fragment>
                            {contactQuery.data?.contacts.map(
                              (contact: TContact) => {
                                return (
                                  <StyledBodyRow
                                    key={contact.id}
                                    tabIndex={0}
                                    onDoubleClick={() =>
                                      handleSelectRow(contact)
                                    }
                                  >
                                    <StyledTableBodyCell>
                                      {contact.First_Name} {contact.Last_Name}
                                    </StyledTableBodyCell>
                                    <StyledTableBodyCell>
                                      {contact.Phone}
                                    </StyledTableBodyCell>
                                    <StyledTableBodyCell>
                                      {contact.Email}
                                    </StyledTableBodyCell>
                                    <StyledTableBodyCell>
                                      <StyledInTableButton
                                        variant="contained"
                                        color="primary"
                                        onClick={() => handleSelectRow(contact)}
                                      >
                                        <FormattedMessage
                                          id="ServiceHolderFindModal.Select"
                                          defaultMessage="Seleccionar"
                                        />
                                      </StyledInTableButton>
                                    </StyledTableBodyCell>
                                  </StyledBodyRow>
                                );
                              }
                            )}
                          </Fragment>
                        )}
                      </TableBody>
                    </StyledTable>
                  </StyledTableWrapper>
                </Fragment>
              )}
            {(formikContext.values.op === "new" ||
              (formikContext.values.op === "existing" &&
                formikContext.values.contact_id)) && (
              <Grid container spacing={8}>
                {formikContext.values.op === "new" &&
                  formikContext.values.email_used_by && (
                    <Grid item xs={12}>
                      <StyledAlert severity="error">
                        <FormattedMessage
                          id="ContactRelationModalForm.ExistingEmail"
                          defaultMessage="El email que estás introduciendo ya existe en nuestra base de datos de contactos para {fullName}"
                          values={{
                            fullName: (
                              <b>
                                {formikContext.values.email_used_by.First_Name}{" "}
                                {formikContext.values.email_used_by.Last_Name}
                              </b>
                            ),
                          }}
                        />
                        {formikContext.values.email_used_by.crm_id && (
                          <Box mt={4}>
                            <ViewInZohoButton
                              zohoModule="Contact"
                              zohoId={formikContext.values.email_used_by.crm_id}
                            />
                          </Box>
                        )}
                      </StyledAlert>
                    </Grid>
                  )}
                {formikContext.values.op === "new" &&
                  formikContext.values.phone_used_by && (
                    <Grid item xs={12}>
                      <StyledAlert severity="error">
                        <FormattedMessage
                          id="ContactRelationModalForm.ExistingPhone"
                          defaultMessage="El teléfono que estás introduciendo ya existe en nuestra base de datos de contactos para {fullName}"
                          values={{
                            fullName: (
                              <b>
                                {formikContext.values.phone_used_by.First_Name}{" "}
                                {formikContext.values.phone_used_by.Last_Name}
                              </b>
                            ),
                          }}
                        />
                        {formikContext.values.phone_used_by.crm_id && (
                          <Box mt={4}>
                            <ViewInZohoButton
                              zohoModule="Contact"
                              zohoId={formikContext.values.phone_used_by.crm_id}
                            />
                          </Box>
                        )}
                      </StyledAlert>
                    </Grid>
                  )}
                <Grid item xs={12} md={6}>
                  <FormikTextField
                    name="First_Name"
                    label={
                      <FormattedMessage
                        id="ContactRelationModal.FirstName"
                        defaultMessage="Nombre"
                      />
                    }
                    required
                    fullWidth
                    size="small"
                    margin="nomargin"
                    disabled={formikContext.values.op === "existing"}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormikTextField
                    name="Last_Name"
                    label={
                      <FormattedMessage
                        id="ContactRelationModal.LastName"
                        defaultMessage="Apellidos"
                      />
                    }
                    required
                    fullWidth
                    size="small"
                    margin="nomargin"
                    disabled={formikContext.values.op === "existing"}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormikTextField
                    name="Phone"
                    type="tel"
                    label={
                      <FormattedMessage
                        id="ContactRelationModal.Phone"
                        defaultMessage="Teléfono"
                      />
                    }
                    required
                    telformat
                    fullWidth
                    size="small"
                    margin="nomargin"
                    disabled={formikContext.values.op === "existing"}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormikTextField
                    name="Email"
                    label={
                      <FormattedMessage
                        id="ContactRelationModal.Email"
                        defaultMessage="Correo electrónico"
                      />
                    }
                    required
                    fullWidth
                    size="small"
                    margin="nomargin"
                    disabled={formikContext.values.op === "existing"}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormikSelect
                    name="Parentesco"
                    label={
                      <FormattedMessage
                        id="ContactRelationModal.Relationship"
                        defaultMessage="Parentesco"
                      />
                    }
                    fullWidth
                    variant="compact"
                    size="small"
                    margin="nomargin"
                    placeholder={formatMessage({
                      id: "Select...",
                      defaultMessage: "Selecciona...",
                    })}
                    options={kinshipOptions}
                    useBodyPortal
                  />
                </Grid>
              </Grid>
            )}
          </div>
        </StyledWrapper>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} variant="outlined">
          <FormattedMessage
            id="ContactRelationModal.Cancel"
            defaultMessage="Cancelar"
          />
        </Button>
        <Button type="submit" variant="contained" color="primary">
          <FormattedMessage
            id="ContactRelationModal.Accept"
            defaultMessage="Aceptar"
          />
        </Button>
      </DialogActions>
    </Fragment>
  );
};

export default ContactRelationModalForm;
